import Image from 'next/image';
import Link from 'next/link';
import { ArrowSmallRightIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { calcMiles, slugify, getStates } from '@/lib/util';

// Create a lookup map from state code to full name
const stateCodeToName = getStates().reduce((acc, state) => {
  acc[state.value] = state.label;
  return acc;
}, {});
import { useState } from 'react';
import clsx from 'clsx';

export default function ParkItem({
  park,
  showDistance,
  isListView,
  compact = false,
}) {
  const [imgError, setImgError] = useState(false);
  const placeholderImage = '/placeholder/placeholder.png';
  const imageUrl = park?.images?.[0]?.url;
  const image = imgError || !imageUrl ? placeholderImage : imageUrl;
  const isExternal = image.startsWith('https://');
  const isPlaceholder = image === placeholderImage;
  const parkUrl = `/park/${park.slug || slugify(park.fullName)}`;

  // Get first 3 activities for display
  const activities =
    park.ParkActivity?.slice(0, 3)
      .map((pa) => pa.Activity?.name)
      .filter(Boolean) || [];

  // Get location
  const city = park.addresses?.[0]?.city;
  const stateCode =
    park.addresses?.[0]?.stateCode || park.states?.split(',')[0];
  const stateName = stateCodeToName[stateCode] || stateCode;
  const location =
    city && stateName ? `${city}, ${stateName}` : stateName || '';

  // Compact mode for homepage (bigger image, less info, NO arrow CTA)
  if (compact) {
    return (
      <Link
        href={parkUrl}
        className="block h-full no-opacity-transition transition hover:shadow-lg rounded-sm overflow-hidden"
      >
        <article className="bg-white h-full rounded-sm border border-brown-100/10 overflow-hidden text-brown-100 text-sm shadow-sm hover:shadow-md">
          <div className="flex flex-col h-full">
            {/* Image */}
            <div className="relative h-[220px] w-full">
              <Image
                src={image}
                alt={park?.images?.[0]?.altText || park.fullName}
                fill
                className={`object-cover ${isPlaceholder ? '' : 'object-top'}`}
                onError={() => setImgError(true)}
                unoptimized={isExternal}
              />
              {park.designation && (
                <p className="text-xxs font-bold absolute bottom-0 left-0 z-1 bg-white py-1 px-3 text-brown-100/70 rounded-tr-xs">
                  {park.designation}
                </p>
              )}
            </div>

            {/* Content */}
            <div className="flex flex-col flex-1 p-4">
              <h2 className="text-lg font-normal leading-tight text-green-100 mb-1">
                {park.fullName}
              </h2>
              {location && (
                <p className="text-xs text-brown-100/60">{location}</p>
              )}
            </div>
          </div>
        </article>
      </Link>
    );
  }

  return (
    <Link
      href={parkUrl}
      className="block h-full no-opacity-transition transition hover:shadow-lg rounded-sm overflow-hidden"
    >
      <article className="bg-white h-full rounded-sm border border-brown-100/10 overflow-hidden text-brown-100 text-sm shadow-sm hover:shadow-md">
        <div
          className={`flex justify-between h-full ${isListView ? 'flex-row' : 'flex-col'}`}
        >
          <div
            className={`flex flex-col ${isListView ? 'md:w-4/6' : 'w-full'}`}
          >
            <div className={`flex ${isListView ? '' : 'flex-col'}`}>
              {/* Image - matches EventItem structure with aspect-square */}
              <div
                className={`${isListView ? 'h-full w-[300px]' : 'w-full h-[200px]'}`}
              >
                <div className="relative h-full block">
                  {park.designation && (
                    <p
                      className={`text-xxs font-bold absolute bottom-0 z-1 bg-white py-1 px-3 text-brown-100/70
                        ${isListView ? 'right-0 rounded-tl-xs' : 'left-0 rounded-tr-xs'}`}
                    >
                      {park.designation}
                    </p>
                  )}
                  <Image
                    src={image}
                    alt={park?.images?.[0]?.altText || park.fullName}
                    width={500}
                    height={500}
                    className={`object-cover w-full h-full z-0 aspect-square ${isPlaceholder ? '' : 'object-top'} ${isListView ? 'absolute top-0 rounded-l-sm' : ''}`}
                    onError={() => setImgError(true)}
                    unoptimized={isExternal}
                  />
                </div>
              </div>

              {/* Content */}
              <div
                className={`basis-full ${isListView ? 'md:py-5 md:px-5' : 'p-5'}`}
              >
                {/* Park Name */}
                <h2 className="text-lg font-normal leading-tight text-green-100 md:text-xl mb-2">
                  {park.fullName}
                </h2>

                {/* Location */}
                {location && (
                  <p className="text-xs text-brown-100/70 mb-2">{location}</p>
                )}

                {/* Distance if available */}
                {park.distanceFrom && showDistance && (
                  <p className="text-xs text-brown-100/60 mb-2">
                    {Math.round(calcMiles(park.distanceFrom))} mi away
                  </p>
                )}

                {/* Activities tags */}
                {activities.length > 0 && (
                  <ul className="flex flex-wrap mt-4">
                    {activities.map((activity, i) => (
                      <li key={`${activity}-${i}`}>
                        <p className="text-xxs my-1 py-1 pb-1 px-3 bg-gray rounded-full mr-2 text-brown-100/80">
                          {activity}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Arrow CTA for list view */}
          {isListView && (
            <div className="flex self-center justify-center px-3 bg-green-100 text-white w-20 h-full">
              <span className="sr-only">View park</span>
              <ArrowSmallRightIcon width={20} className="self-center" />
            </div>
          )}
        </div>

        {/* Arrow CTA for grid view */}
        {!isListView && (
          <div className="flex justify-center bg-green-100 text-white h-10">
            <span className="sr-only">View park</span>
            <ArrowSmallRightIcon width={20} />
          </div>
        )}
      </article>
    </Link>
  );
}
